import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Phone, ArrowRight } from "lucide-react";

export default function FinalCTA() {
  return (
    <section id="cta" data-testid="cta-section" className="py-24 md:py-48 relative overflow-hidden">
      <div className="section-divider" />

      {/* Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Central aurora */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[600px] aurora-blue animate-aurora-1"
          style={{ opacity: 0.8 }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] aurora-violet animate-aurora-2"
          style={{ opacity: 0.7 }}
        />
        {/* Dot grid */}
        <div className="absolute inset-0 dot-grid opacity-[0.18]" />
        {/* Edge fades */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#07070A] via-transparent to-[#07070A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07070A]/60 via-transparent to-[#07070A]/60" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] mb-10 backdrop-blur-sm"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-violet-400" style={{ boxShadow: "0 0 6px rgba(167,139,250,0.8)" }} />
          <span className="text-[11px] font-mono tracking-[0.18em] text-white/45 uppercase">Get Started Today</span>
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-black tracking-[-0.045em] text-white mb-7 leading-[0.92]"
          style={{ fontSize: "clamp(2.6rem, 7vw, 5.5rem)" }}
        >
          Ready to Automate
          <br />
          <span className="gradient-text">Your Business?</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="text-[16px] sm:text-[17px] text-white/40 max-w-xl mx-auto mb-12 leading-[1.7] tracking-[-0.01em]"
        >
          Book a free 30-minute strategy call. We'll map out the highest-ROI automation
          opportunities in your business — no obligation, no sales pitch.
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row gap-3.5 justify-center"
        >
          {/* WhatsApp — primary */}
          <div className="relative group">
            <div className="absolute -inset-1.5 rounded-2xl bg-emerald-500/15 blur-xl group-hover:bg-emerald-500/25 transition-all duration-500" />
            <a
              href="https://wa.me/918248195127?text=Hi%20AIFlow%20Labs!%20I%20want%20to%20book%20a%20free%20strategy%20call."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-btn"
              className="btn-shine relative inline-flex items-center justify-center gap-2.5 px-8 py-[15px] rounded-xl bg-gradient-to-b from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-semibold text-[14px] tracking-[-0.01em] transition-all duration-200 shadow-[0_1px_0_rgba(255,255,255,0.15)_inset,0_8px_32px_rgba(16,185,129,0.22)] hover:shadow-[0_1px_0_rgba(255,255,255,0.2)_inset,0_12px_40px_rgba(16,185,129,0.32)]"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </a>
          </div>

          {/* Book Call — secondary */}
          <a
            href="https://wa.me/918248195127?text=Hi%20AIFlow%20Labs!%20I%27d%20like%20to%20book%20a%20strategy%20call."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-book-call-btn"
            className="group inline-flex items-center justify-center gap-2 px-8 py-[15px] rounded-xl border border-white/[0.09] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/[0.14] text-white/75 hover:text-white font-semibold text-[14px] tracking-[-0.01em] transition-all duration-200 backdrop-blur-sm"
          >
            <Phone className="w-4 h-4" />
            Book a Strategy Call
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </motion.div>

        {/* Fine print */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.45 }}
          className="mt-9 text-[11px] text-white/18 font-mono tracking-[0.05em]"
        >
          Free consultation · No commitment required · Response within 2 hours
        </motion.p>
      </div>
    </section>
  );
}
